import { NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  LayoutDashboard,
  Images,
  Calendar,
  Mic2,
  Users,
  Heart,
  FolderOpen,
  Settings,
  MessageSquare,
  LogOut,
  ChevronLeft,
  Menu,
  UserCog,
  Camera,
  Newspaper,
  UserCheck,
  Network,
  Megaphone,
  CalendarCheck,
  FolderDown,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useCollection } from '../../hooks/useFirestore'
import { cn } from '../../utils/helpers'

const navItems = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, end: true },
  { name: 'Hero Slides', path: '/admin/hero', icon: Images },
  { name: 'Events', path: '/admin/events', icon: Calendar },
  { name: 'Sermons', path: '/admin/sermons', icon: Mic2 },
  { name: 'Blog', path: '/admin/blog', icon: Newspaper },
  { name: 'Gallery', path: '/admin/gallery', icon: Camera },
  { name: 'Ministries', path: '/admin/ministries', icon: Heart },
  { name: 'Leadership', path: '/admin/leadership', icon: Users },
  { name: 'Media Library', path: '/admin/media', icon: FolderOpen },
  { name: 'Submissions', path: '/admin/submissions', icon: MessageSquare },
  { name: 'Users', path: '/admin/users', icon: UserCog },
  { name: 'Settings', path: '/admin/settings', icon: Settings },
]

const portalNavItems = [
  { name: 'Workers', path: '/admin/workers', icon: UserCheck },
  { name: 'Departments', path: '/admin/departments', icon: Network },
  { name: 'Announcements', path: '/admin/announcements', icon: Megaphone },
  { name: 'Schedules', path: '/admin/schedules', icon: CalendarCheck },
  { name: 'Resources', path: '/admin/resources', icon: FolderDown },
]

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const { signOut, user, userRole } = useAuth()
  const { data: workerDocs } = useCollection('users', {
    orderByField: null,
    whereClause: { field: 'role', operator: '==', value: 'worker' },
  })
  const pendingCount = (workerDocs || []).filter((w) => w.status === 'pending').length

  return (
    <motion.aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-dark text-white flex flex-col z-40 transition-all duration-300',
        isCollapsed ? 'w-20' : 'w-64'
      )}
      initial={false}
      animate={{ width: isCollapsed ? 80 : 256 }}
    >
      {/* Logo */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-dark-200">
        {!isCollapsed && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold">DL</span>
            </div>
            <div>
              <span className="font-heading font-bold text-sm">DLBC Admin</span>
            </div>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-dark-200 rounded-lg transition-colors"
        >
          {isCollapsed ? (
            <Menu className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <ul className="space-y-1 px-3">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-400 hover:bg-dark-200 hover:text-white'
                  )
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && (
                  <span className="font-medium">{item.name}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {!isCollapsed && (
          <p className="px-6 pt-6 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Workers Portal
          </p>
        )}
        {isCollapsed && <div className="mx-4 my-4 border-t border-dark-200" />}
        <ul className="space-y-1 px-3">
          {portalNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-400 hover:bg-dark-200 hover:text-white'
                  )
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && (
                  <span className="font-medium flex-1">{item.name}</span>
                )}
                {!isCollapsed && item.path === '/admin/workers' && pendingCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500 text-white">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-dark-200">
        {!isCollapsed && (
          <div className="mb-3 px-3">
            <p className="text-sm text-gray-400">Logged in as</p>
            <p className="text-sm font-medium truncate">{user?.email}</p>
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs bg-primary-600/20 text-primary-300">
              {userRole === 'super_admin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Editor'}
            </span>
          </div>
        )}
        <button
          onClick={signOut}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-3 rounded-lg text-gray-400 hover:bg-dark-200 hover:text-white transition-colors',
            isCollapsed && 'justify-center'
          )}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="font-medium">Sign Out</span>}
        </button>
      </div>
    </motion.aside>
  )
}

export default Sidebar
